import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Shield, Gem, TreePine, Train, MapPin, Building2 } from "lucide-react";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_buyer-portal-13/artifacts/s92nxbhh_WhatsApp%20Image%202026-03-22%20at%2022.06.01.jpeg";
const ABOUT_IMG = "https://customer-assets.emergentagent.com/job_buyer-portal-13/artifacts/iuvluewy_WhatsApp%20Image%202026-03-22%20at%2022.06.26.jpeg";
const AMENITY_IMG = "https://images.pexels.com/photos/6467627/pexels-photo-6467627.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const LOCATION_IMG = "https://images.pexels.com/photos/4977842/pexels-photo-4977842.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const useInView = () => {
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("animate-fade-up");
          entry.target.style.opacity = "1";
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return ref;
};

const AnimatedSection = ({ children, className = "", delay = "" }) => {
  const ref = useInView();
  return (
    <div ref={ref} className={`opacity-0 ${delay} ${className}`}>
      {children}
    </div>
  );
};

export default function HomePage() {
  return (
    <div data-testid="home-page" className="min-h-screen" style={{ background: "#050505" }}>
      {/* Hero */}
      <section data-testid="hero-section" className="relative h-screen flex items-end">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Elysian Heights Exterior"
            className="w-full h-full object-cover"
          />
          <div className="hero-overlay absolute inset-0" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 pb-24 md:pb-32 w-full">
          <div className="animate-fade-up">
            <p
              className="text-xs tracking-[0.3em] uppercase mb-6"
              style={{ color: "#D4AF37", fontFamily: "'Manrope', sans-serif" }}
            >
              A New Chapter in Coulsdon
            </p>
            <h1
              className="text-5xl sm:text-6xl lg:text-8xl font-medium tracking-tight leading-none mb-8"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Unrivaled
              <br />
              <span className="italic" style={{ color: "#D4AF37" }}>
                Elegance
              </span>
            </h1>
            <p
              className="text-base md:text-lg leading-relaxed max-w-lg mb-10"
              style={{ color: "#A3A3A3", fontFamily: "'Manrope', sans-serif" }}
            >
              Discover bespoke residences crafted for those who appreciate the
              extraordinary. Elysian Heights redefines luxury living in the
              heart of Coulsdon.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/register"
                data-testid="hero-register-btn"
                className="inline-flex items-center gap-3 px-8 py-4 text-xs tracking-[0.2em] uppercase font-semibold transition-all duration-300 hover:-translate-y-1"
                style={{ background: "#D4AF37", color: "#000", fontFamily: "'Manrope', sans-serif" }}
              >
                Register Your Interest
                <ArrowRight size={16} strokeWidth={1.5} />
              </Link>
              <a
                href="#about"
                data-testid="hero-discover-btn"
                className="inline-flex items-center gap-3 px-8 py-4 text-xs tracking-[0.2em] uppercase font-semibold transition-all duration-300 hover:-translate-y-1"
                style={{
                  border: "1px solid rgba(212,175,55,0.4)",
                  color: "#D4AF37",
                  fontFamily: "'Manrope', sans-serif",
                }}
              >
                Discover More
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section id="about" data-testid="about-section" className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <AnimatedSection>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              <div>
                <p
                  className="text-xs tracking-[0.2em] uppercase font-bold mb-4"
                  style={{ color: "#D4AF37", fontFamily: "'Manrope', sans-serif" }}
                >
                  The Vision
                </p>
                <h2
                  className="text-4xl md:text-6xl font-normal tracking-tight mb-8"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Two Brand New Luxury
                  <br />
                  <span className="italic" style={{ color: "#D4AF37" }}>
                    4-Bedroom Detached Homes
                  </span>
                </h2>
                <div className="section-line mb-8" />
                <p
                  className="text-base md:text-lg leading-relaxed mb-6"
                  style={{ color: "#A3A3A3", fontFamily: "'Manrope', sans-serif" }}
                >
                  Elysian Heights presents two exceptional 4-bedroom detached
                  residences in the heart of Coulsdon. Designed for discerning
                  families, each home combines generous living spaces with
                  uncompromising craftsmanship and attention to detail.
                </p>
                <p
                  className="text-base md:text-lg leading-relaxed"
                  style={{ color: "#A3A3A3", fontFamily: "'Manrope', sans-serif" }}
                >
                  Every detail has been meticulously considered — from the
                  premium kitchen finishes to the expansive private gardens and
                  light-filled interiors that define truly distinguished living.
                </p>
              </div>
              <div className="relative">
                <img
                  src={ABOUT_IMG}
                  alt="Luxury Interior"
                  className="w-full h-[500px] object-cover"
                  style={{ filter: "brightness(0.9)" }}
                />
                <div
                  className="absolute -bottom-6 -left-6 px-8 py-6 glass-card"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  <p className="text-3xl italic" style={{ color: "#D4AF37" }}>
                    Est. 2025
                  </p>
                  <p className="text-sm mt-1" style={{ color: "#A3A3A3" }}>
                    Completion Expected June 2026
                  </p>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Features */}
      <section
        id="features"
        data-testid="features-section"
        className="py-24 md:py-32 relative"
      >
        <div className="gold-spotlight absolute inset-0" />
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
          <AnimatedSection>
            <div className="text-center mb-16 md:mb-24">
              <p
                className="text-xs tracking-[0.2em] uppercase font-bold mb-4"
                style={{ color: "#D4AF37", fontFamily: "'Manrope', sans-serif" }}
              >
                Curated Living
              </p>
              <h2
                className="text-4xl md:text-6xl font-normal tracking-tight"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Beyond <span className="italic" style={{ color: "#D4AF37" }}>Expectation</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                {
                  icon: Building2,
                  title: "Bespoke Interiors",
                  desc: "Handcrafted kitchens, engineered oak flooring, and premium fixtures throughout every residence.",
                },
                {
                  icon: TreePine,
                  title: "Landscaped Gardens",
                  desc: "Thoughtfully designed green spaces offering a serene retreat from the pace of daily life.",
                },
                {
                  icon: Shield,
                  title: "Secure Parking",
                  desc: "Off-street parking with electric vehicle charging points and secure bicycle storage.",
                },
                {
                  icon: Gem,
                  title: "Smart Home Tech",
                  desc: "Integrated home automation systems including climate, lighting, and security control.",
                },
              ].map((feature, i) => (
                <div
                  key={i}
                  className="glass-card p-8 md:p-10 hover-lift gold-border-hover"
                  data-testid={`feature-card-${i}`}
                >
                  <feature.icon
                    size={28}
                    strokeWidth={1}
                    style={{ color: "#D4AF37" }}
                    className="mb-6"
                  />
                  <h3
                    className="text-xl md:text-2xl font-light tracking-wide mb-4"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {feature.title}
                  </h3>
                  <p
                    className="text-sm leading-relaxed"
                    style={{ color: "#A3A3A3", fontFamily: "'Manrope', sans-serif" }}
                  >
                    {feature.desc}
                  </p>
                </div>
              ))}
            </div>
          </AnimatedSection>

          {/* Amenity image strip */}
          <AnimatedSection className="mt-16 md:mt-24">
            <div className="relative h-[300px] md:h-[400px] overflow-hidden">
              <img
                src={AMENITY_IMG}
                alt="Residents Lounge"
                className="w-full h-full object-cover"
                style={{ filter: "brightness(0.8)" }}
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <p
                  className="text-3xl md:text-5xl italic text-center px-6"
                  style={{ fontFamily: "'Cormorant Garamond', serif", color: "#F5F5F5" }}
                >
                  "Where every detail whispers luxury"
                </p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Location */}
      <section id="location" data-testid="location-section" className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <AnimatedSection>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              <div className="order-2 lg:order-1">
                <div className="relative h-[400px] md:h-[500px] overflow-hidden">
                  <img
                    src={LOCATION_IMG}
                    alt="Coulsdon Surrey"
                    className="w-full h-full object-cover"
                    style={{ filter: "brightness(0.85)" }}
                  />
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <p
                  className="text-xs tracking-[0.2em] uppercase font-bold mb-4"
                  style={{ color: "#D4AF37", fontFamily: "'Manrope', sans-serif" }}
                >
                  Prime Location
                </p>
                <h2
                  className="text-4xl md:text-6xl font-normal tracking-tight mb-8"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  The Heart of{" "}
                  <span className="italic" style={{ color: "#D4AF37" }}>
                    Coulsdon
                  </span>
                </h2>
                <div className="section-line mb-8" />
                <p
                  className="text-base md:text-lg leading-relaxed mb-8"
                  style={{ color: "#A3A3A3", fontFamily: "'Manrope', sans-serif" }}
                >
                  Perfectly positioned between the vibrant culture of London and
                  the serenity of the Surrey Hills, Coulsdon offers an
                  exceptional quality of life.
                </p>
                <div className="flex flex-col gap-5">
                  {[
                    { icon: Train, label: "25 mins to Central London via rail" },
                    { icon: MapPin, label: "Adjacent to the Surrey Hills AONB" },
                    { icon: TreePine, label: "Farthing Downs & Happy Valley" },
                    { icon: Building2, label: "Excellent local schools & amenities" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-4" data-testid={`location-point-${i}`}>
                      <div
                        className="w-10 h-10 flex items-center justify-center"
                        style={{ background: "rgba(212,175,55,0.1)" }}
                      >
                        <item.icon size={18} strokeWidth={1} style={{ color: "#D4AF37" }} />
                      </div>
                      <p className="text-sm md:text-base" style={{ color: "#E5E5E5", fontFamily: "'Manrope', sans-serif" }}>
                        {item.label}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="cta-section" className="py-24 md:py-32 relative">
        <div className="gold-spotlight absolute inset-0" />
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10 text-center">
          <AnimatedSection>
            <p
              className="text-xs tracking-[0.2em] uppercase font-bold mb-6"
              style={{ color: "#D4AF37", fontFamily: "'Manrope', sans-serif" }}
            >
              Begin Your Journey
            </p>
            <h2
              className="text-4xl md:text-6xl font-normal tracking-tight mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Secure Your{" "}
              <span className="italic" style={{ color: "#D4AF37" }}>
                Legacy
              </span>
            </h2>
            <p
              className="text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-10"
              style={{ color: "#A3A3A3", fontFamily: "'Manrope', sans-serif" }}
            >
              Register your interest today to receive priority access, exclusive
              updates, and an invitation to our private viewing events.
            </p>
            <Link
              to="/register"
              data-testid="cta-register-btn"
              className="inline-flex items-center gap-3 px-10 py-4 text-xs tracking-[0.2em] uppercase font-semibold transition-all duration-300 hover:-translate-y-1"
              style={{ background: "#D4AF37", color: "#000", fontFamily: "'Manrope', sans-serif" }}
            >
              Register Your Interest
              <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
